import { Link, createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/convex-api";

type SupportStatus = "open" | "waiting_for_user" | "resolved";

type SupportAttachment = {
	_id: string;
	attachmentType: "document" | "image" | "pdf";
	fileName: string;
	url: string | null;
};

type SupportMessage = {
	_id: string;
	attachments: SupportAttachment[];
	authorName: string;
	authorType: "admin" | "system" | "user";
	body: string;
	createdAt: number;
};

type SupportNote = {
	_id: string;
	authorName: string;
	body: string;
	createdAt: number;
};

type SupportTicketDetail = {
	adminLastReadAt?: number;
	category: string;
	contextSnapshot: {
		appVersion?: string;
		buildProfile?: string;
		deviceModel?: string;
		entrypoint: string;
		osVersion?: string;
		platform: string;
		runtimeVersion?: string;
		sourceScreen: string;
	};
	disputeId?: string;
	errandId?: string;
	hasUnread: boolean;
	linkedSnapshot?: {
		amountKobo?: number;
		kind: string;
		status?: string;
		subtitle?: string;
		title?: string;
	};
	privateTaskId?: string;
	requesterUserId: string;
	requesterSnapshot: {
		email: string;
		name: string;
		phoneNumber?: string;
	};
	runnerProfileId?: string;
	sourceApp: string;
	status: SupportStatus;
	subject: string;
	transactionId?: string;
	updatedAt: number;
};

type SupportAdminResponse = {
	messages: SupportMessage[];
	notes: SupportNote[];
	ticket: SupportTicketDetail;
};

export const Route = createFileRoute("/_authenticated/support/$ticketId")({
	component: SupportDetailPage,
});

function formatTimestamp(timestamp: number): string {
	return new Date(timestamp).toLocaleString();
}

function formatNaira(kobo?: number): string | null {
	if (typeof kobo !== "number") return null;
	return `₦${(kobo / 100).toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;
}

function statusVariant(
	status: string,
): "default" | "destructive" | "outline" | "secondary" {
	switch (status) {
		case "resolved":
			return "default";
		case "waiting_for_user":
			return "secondary";
		default:
			return "outline";
	}
}

function SupportDetailPage() {
	const { ticketId } = Route.useParams();
	const data = useQuery(api.support.getAdminTicket, {
		ticketId: ticketId as never,
	}) as SupportAdminResponse | null | undefined;
	const markAdminRead = useMutation(api.support.markAdminRead);
	const replyAsAdmin = useMutation(api.support.replyAsAdmin);
	const updateStatus = useMutation(api.support.updateStatus);
	const addInternalNote = useMutation(api.support.addInternalNote);

	const [replyBody, setReplyBody] = useState("");
	const [replyStatus, setReplyStatus] =
		useState<SupportStatus>("waiting_for_user");
	const [statusDraft, setStatusDraft] = useState<SupportStatus>("open");
	const [noteBody, setNoteBody] = useState("");
	const [isReplying, setIsReplying] = useState(false);
	const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
	const [isAddingNote, setIsAddingNote] = useState(false);

	useEffect(() => {
		if (!data?.ticket) {
			return;
		}

		setStatusDraft(data.ticket.status);
		setReplyStatus(
			data.ticket.status === "resolved" ? "open" : "waiting_for_user",
		);
		if (data.ticket.hasUnread) {
			void markAdminRead({ ticketId: ticketId as never });
		}
	}, [data?.ticket?.hasUnread, data?.ticket?.status, markAdminRead, ticketId]);

	const linkedTargets = useMemo(() => {
		if (!data?.ticket) {
			return [];
		}

		const items: Array<{ href: string; label: string }> = [];

		if (data.ticket.errandId) {
			items.push({
				href: `/errands/${data.ticket.errandId}`,
				label: "Open errand",
			});
		}
		if (data.ticket.disputeId) {
			items.push({
				href: `/disputes/${data.ticket.disputeId}`,
				label: "Open dispute",
			});
		}
		if (data.ticket.runnerProfileId) {
			items.push({
				href: `/runners/${data.ticket.requesterUserId}`,
				label: "Open runner",
			});
		}
		if (data.ticket.transactionId) {
			items.push({
				href: "/finances",
				label: "Open finances",
			});
		}
		if (data.ticket.privateTaskId) {
			items.push({
				href: "/private",
				label: "Open private tasks",
			});
		}

		return items;
	}, [data?.ticket]);

	const handleReply = async () => {
		if (!replyBody.trim()) {
			toast.error("Add a reply before sending.");
			return;
		}

		setIsReplying(true);
		try {
			await replyAsAdmin({
				ticketId: ticketId as never,
				body: replyBody.trim(),
				statusAfterReply: replyStatus,
			});
			setReplyBody("");
			toast.success("Reply sent.");
		} catch {
			toast.error("Couldn't send reply.");
		} finally {
			setIsReplying(false);
		}
	};

	const handleStatusUpdate = async () => {
		setIsUpdatingStatus(true);
		try {
			await updateStatus({
				ticketId: ticketId as never,
				status: statusDraft,
			});
			toast.success("Status updated.");
		} catch {
			toast.error("Couldn't update status.");
		} finally {
			setIsUpdatingStatus(false);
		}
	};

	const handleAddNote = async () => {
		if (!noteBody.trim()) {
			toast.error("Add a note before saving.");
			return;
		}

		setIsAddingNote(true);
		try {
			await addInternalNote({
				ticketId: ticketId as never,
				body: noteBody.trim(),
			});
			setNoteBody("");
			toast.success("Internal note added.");
		} catch {
			toast.error("Couldn't add note.");
		} finally {
			setIsAddingNote(false);
		}
	};

	if (data === undefined) {
		return (
			<div className="container mx-auto px-4 py-10 lg:px-6">
				<Skeleton className="h-8 w-48" />
				<div className="mt-8 grid gap-8 xl:grid-cols-[minmax(0,1fr)_320px]">
					<Skeleton className="h-[520px]" />
					<Skeleton className="h-[520px]" />
				</div>
			</div>
		);
	}

	if (!data) {
		return (
			<div className="container mx-auto px-4 py-10 lg:px-6">
				<p className="text-muted-foreground">Support thread not found.</p>
				<Button asChild className="mt-4" variant="outline">
					<Link to="/support">Back to Support</Link>
				</Button>
			</div>
		);
	}

	return (
		<div className="container mx-auto px-4 py-10 lg:px-6">
			<div className="mb-8 flex flex-wrap items-center gap-3">
				<Button asChild size="sm" variant="ghost">
					<Link to="/support">Back</Link>
				</Button>
				<h1 className="min-w-0 flex-1 truncate font-bold text-2xl">
					{data.ticket.subject}
				</h1>
				<Badge variant={statusVariant(data.ticket.status)}>
					{data.ticket.status.replaceAll("_", " ")}
				</Badge>
			</div>

			<div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_320px]">
				<div className="space-y-6">
					<section className="overflow-hidden rounded-xl border">
						<div className="border-b px-5 py-4">
							<h2 className="font-semibold text-sm uppercase tracking-[0.14em] text-muted-foreground">
								Thread
							</h2>
						</div>
						<div className="divide-y">
							{data.messages.map((message) => {
								const isAdmin = message.authorType === "admin";
								return (
									<div className="px-5 py-4" key={message._id}>
										<div className="flex items-start justify-between gap-3">
											<div>
												<p className="font-medium text-sm">
													{isAdmin
														? `Support • ${message.authorName}`
														: message.authorName}
												</p>
												<p className="mt-1 text-muted-foreground text-xs">
													{formatTimestamp(message.createdAt)}
												</p>
											</div>
											<Badge variant={isAdmin ? "secondary" : "outline"}>
												{message.authorType}
											</Badge>
										</div>
										<p className="mt-3 whitespace-pre-wrap text-sm leading-6">
											{message.body}
										</p>
										{message.attachments.length > 0 ? (
											<div className="mt-4 flex flex-wrap gap-2">
												{message.attachments.map((attachment) =>
													attachment.url ? (
														<a
															className="inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-medium hover:bg-accent"
															href={attachment.url}
															key={attachment._id}
															rel="noreferrer"
															target="_blank"
														>
															{attachment.fileName}
														</a>
													) : null,
												)}
											</div>
										) : null}
									</div>
								);
							})}
						</div>
					</section>

					<section className="overflow-hidden rounded-xl border">
						<div className="border-b px-5 py-4">
							<h2 className="font-semibold text-sm uppercase tracking-[0.14em] text-muted-foreground">
								Reply
							</h2>
						</div>
						<div className="space-y-4 px-5 py-5">
							<Textarea
								onChange={(event) => setReplyBody(event.target.value)}
								placeholder="Reply as support"
								rows={6}
								value={replyBody}
							/>
							<div className="grid gap-3 md:grid-cols-[220px_minmax(0,1fr)]">
								<Select
									onValueChange={(value) =>
										setReplyStatus(value as SupportStatus)
									}
									value={replyStatus}
								>
									<SelectTrigger>
										<SelectValue placeholder="Reply status" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="open">Keep open</SelectItem>
										<SelectItem value="waiting_for_user">
											Waiting for user
										</SelectItem>
										<SelectItem value="resolved">Resolve ticket</SelectItem>
									</SelectContent>
								</Select>
								<div className="flex justify-end">
									<Button
										disabled={isReplying}
										onClick={() => void handleReply()}
									>
										{isReplying ? "Sending..." : "Send Reply"}
									</Button>
								</div>
							</div>
						</div>
					</section>
				</div>

				<aside className="space-y-6">
					<section className="overflow-hidden rounded-xl border">
						<div className="border-b px-5 py-4">
							<h2 className="font-semibold text-sm uppercase tracking-[0.14em] text-muted-foreground">
								Status
							</h2>
						</div>
						<div className="space-y-4 px-5 py-5">
							<Select
								onValueChange={(value) =>
									setStatusDraft(value as SupportStatus)
								}
								value={statusDraft}
							>
								<SelectTrigger>
									<SelectValue placeholder="Ticket status" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="open">Open</SelectItem>
									<SelectItem value="waiting_for_user">
										Waiting for user
									</SelectItem>
									<SelectItem value="resolved">Resolved</SelectItem>
								</SelectContent>
							</Select>
							<Button
								className="w-full"
								disabled={isUpdatingStatus}
								onClick={() => void handleStatusUpdate()}
								variant="outline"
							>
								{isUpdatingStatus ? "Saving..." : "Update Status"}
							</Button>
						</div>
					</section>

					<section className="overflow-hidden rounded-xl border">
						<div className="border-b px-5 py-4">
							<h2 className="font-semibold text-sm uppercase tracking-[0.14em] text-muted-foreground">
								Requester
							</h2>
						</div>
						<div className="space-y-3 px-5 py-5 text-sm">
							<div>
								<p className="text-muted-foreground text-xs uppercase tracking-[0.12em]">
									Name
								</p>
								<p className="mt-1">{data.ticket.requesterSnapshot.name}</p>
							</div>
							<div>
								<p className="text-muted-foreground text-xs uppercase tracking-[0.12em]">
									Email
								</p>
								<p className="mt-1">{data.ticket.requesterSnapshot.email}</p>
							</div>
							{data.ticket.requesterSnapshot.phoneNumber ? (
								<div>
									<p className="text-muted-foreground text-xs uppercase tracking-[0.12em]">
										Phone
									</p>
									<p className="mt-1">
										{data.ticket.requesterSnapshot.phoneNumber}
									</p>
								</div>
							) : null}
							<div className="grid grid-cols-2 gap-3 pt-2">
								<div>
									<p className="text-muted-foreground text-xs uppercase tracking-[0.12em]">
										App
									</p>
									<p className="mt-1 capitalize">{data.ticket.sourceApp}</p>
								</div>
								<div>
									<p className="text-muted-foreground text-xs uppercase tracking-[0.12em]">
										Category
									</p>
									<p className="mt-1">
										{data.ticket.category.replaceAll("_", " ")}
									</p>
								</div>
							</div>
						</div>
					</section>

					<section className="overflow-hidden rounded-xl border">
						<div className="border-b px-5 py-4">
							<h2 className="font-semibold text-sm uppercase tracking-[0.14em] text-muted-foreground">
								Context
							</h2>
						</div>
						<div className="space-y-4 px-5 py-5 text-sm">
							{data.ticket.linkedSnapshot ? (
								<div>
									<p className="text-muted-foreground text-xs uppercase tracking-[0.12em]">
										Linked item
									</p>
									<p className="mt-1 font-medium">
										{data.ticket.linkedSnapshot.title ??
											data.ticket.linkedSnapshot.kind}
									</p>
									{data.ticket.linkedSnapshot.subtitle ? (
										<p className="mt-1 text-muted-foreground">
											{data.ticket.linkedSnapshot.subtitle}
										</p>
									) : null}
									{formatNaira(data.ticket.linkedSnapshot.amountKobo) ? (
										<p className="mt-1 text-muted-foreground">
											{formatNaira(data.ticket.linkedSnapshot.amountKobo)}
										</p>
									) : null}
								</div>
							) : (
								<p className="text-muted-foreground">No linked context</p>
							)}

							{linkedTargets.length > 0 ? (
								<div className="flex flex-wrap gap-2">
									{linkedTargets.map((target) => (
										<Button
											asChild
											key={`${target.href}-${target.label}`}
											size="sm"
											variant="outline"
										>
											<a href={target.href}>{target.label}</a>
										</Button>
									))}
								</div>
							) : null}
						</div>
					</section>

					<section className="overflow-hidden rounded-xl border">
						<div className="border-b px-5 py-4">
							<h2 className="font-semibold text-sm uppercase tracking-[0.14em] text-muted-foreground">
								Diagnostics
							</h2>
						</div>
						<div className="space-y-3 px-5 py-5 text-sm">
							<div>
								<p className="text-muted-foreground text-xs uppercase tracking-[0.12em]">
									Entrypoint
								</p>
								<p className="mt-1">{data.ticket.contextSnapshot.entrypoint}</p>
							</div>
							<div>
								<p className="text-muted-foreground text-xs uppercase tracking-[0.12em]">
									Source screen
								</p>
								<p className="mt-1">
									{data.ticket.contextSnapshot.sourceScreen}
								</p>
							</div>
							<div className="grid grid-cols-2 gap-3">
								<div>
									<p className="text-muted-foreground text-xs uppercase tracking-[0.12em]">
										Platform
									</p>
									<p className="mt-1">{data.ticket.contextSnapshot.platform}</p>
								</div>
								<div>
									<p className="text-muted-foreground text-xs uppercase tracking-[0.12em]">
										Version
									</p>
									<p className="mt-1">
										{data.ticket.contextSnapshot.appVersion ?? "Unknown"}
									</p>
								</div>
							</div>
							<div>
								<p className="text-muted-foreground text-xs uppercase tracking-[0.12em]">
									Device
								</p>
								<p className="mt-1">
									{data.ticket.contextSnapshot.deviceModel ?? "Unknown"}
								</p>
							</div>
							<div>
								<p className="text-muted-foreground text-xs uppercase tracking-[0.12em]">
									Build profile
								</p>
								<p className="mt-1">
									{data.ticket.contextSnapshot.buildProfile ?? "Unknown"}
								</p>
							</div>
						</div>
					</section>

					<section className="overflow-hidden rounded-xl border">
						<div className="border-b px-5 py-4">
							<h2 className="font-semibold text-sm uppercase tracking-[0.14em] text-muted-foreground">
								Internal notes
							</h2>
						</div>
						<div className="space-y-4 px-5 py-5">
							<Textarea
								onChange={(event) => setNoteBody(event.target.value)}
								placeholder="Add private context for the team"
								rows={4}
								value={noteBody}
							/>
							<div className="flex justify-end">
								<Button
									disabled={isAddingNote}
									onClick={() => void handleAddNote()}
									size="sm"
									variant="outline"
								>
									{isAddingNote ? "Saving..." : "Add Note"}
								</Button>
							</div>
							<div className="divide-y border-t">
								{data.notes.length === 0 ? (
									<p className="pt-4 text-muted-foreground text-sm">
										No internal notes yet.
									</p>
								) : (
									data.notes.map((note) => (
										<div className="py-4" key={note._id}>
											<div className="flex items-center justify-between gap-3">
												<p className="font-medium text-sm">{note.authorName}</p>
												<p className="text-muted-foreground text-xs">
													{formatTimestamp(note.createdAt)}
												</p>
											</div>
											<p className="mt-2 whitespace-pre-wrap text-sm leading-6">
												{note.body}
											</p>
										</div>
									))
								)}
							</div>
						</div>
					</section>
				</aside>
			</div>
		</div>
	);
}
